import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Block } from '../../models/block';
import { TrafficRoutes } from '../../models/traffic-patterns';
import { updateBlockDynamicProperties } from '../../store/game/gameSlice';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Divider } from '@mui/material';

interface Props {
  block: Block;
  connectedBlocks: Block[];
  trafficRoutes: TrafficRoutes;
}

export const LoadBalancerSettings: React.FC<Props> = ({ block, connectedBlocks, trafficRoutes }) => {
  const dispatch = useDispatch();
  // routeAssignments: { [routeCategory]: { [routeId]: targetBlockId } }
  const [routeAssignments, setRouteAssignments] = useState<any>(block.dynamicProperties?.routeAssignments || {});

  const handleAssign = (category: string, routeId: string, targetBlockId: string) => {
    const next = {
      ...routeAssignments,
      [category]: {
        ...(routeAssignments[category] || {}),
        [routeId]: targetBlockId
      }
    };
    setRouteAssignments(next);
    
    // Auto-save on change
    dispatch(updateBlockDynamicProperties({
      blockId: block.id,
      properties: { routeAssignments: next }
    }));
  };

  const renderCategory = (title: string, category: keyof TrafficRoutes) => {
    if (!trafficRoutes[category] || trafficRoutes[category].length === 0) return null;
    return (
        <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">{title}</Typography>
        {trafficRoutes[category].map(route => (
            <Box key={route.id} sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="body2" sx={{ width: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {route.path} (Wt: {route.weight})
            </Typography>
            <FormControl size="small" sx={{ flexGrow: 1 }}>
                <InputLabel>Target Block</InputLabel>
                <Select
                value={routeAssignments[category]?.[route.id] || ''}
                label="Target Block"
                onChange={(e) => handleAssign(category, route.id, e.target.value as string)}
                >
                <MenuItem value=""><em>Unassigned (Distribute Evenly)</em></MenuItem>
                {connectedBlocks.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.customName || b.id}</MenuItem>
                ))}
                </Select>
            </FormControl>
            </Box>
        ))}
        </Box>
    );
  };

  if (connectedBlocks.length === 0) {
      return (
          <Typography variant="body2" color="textSecondary">
              Connect this load balancer to other blocks to assign traffic routes.
          </Typography>
      );
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" paragraph>
          Assign globally configured traffic routes to connected output blocks. Routes without an assignment will be distributed evenly among all connected blocks.
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {renderCategory('Static Read Routes', 'staticRead')}
      {renderCategory('Dynamic Read Routes', 'dynamicRead')}
      {renderCategory('Dynamic Write Routes', 'dynamicWrite')}
    </Box>
  );
};
